import { useState, useEffect } from 'react';
import adminService from '../../services/admin';
import toast from 'react-hot-toast';

function SiteContentManagement() {
  const [contentItems, setContentItems] = useState([]);
  const [contentKeys, setContentKeys] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [selectedItem, setSelectedItem] = useState(null);
  const [showEditor, setShowEditor] = useState(false);

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    setIsLoading(true);
    try {
      const [contentData, keysData] = await Promise.all([
        adminService.getSiteContent(),
        adminService.getContentKeys(),
      ]);
      setContentItems(contentData.items);
      setContentKeys(keysData.keys);
    } catch (error) {
      toast.error('Failed to load content');
    } finally {
      setIsLoading(false);
    }
  };

  const handleEdit = (item) => {
    setSelectedItem(item);
    setShowEditor(true);
  };

  const handleCreateFromKey = (key) => {
    setSelectedItem({ key: key.key, title: key.name, isNew: true });
    setShowEditor(true);
  };

  const handleSave = async (data) => {
    try {
      if (selectedItem?.isNew) {
        await adminService.upsertContentByKey(selectedItem.key, data);
        toast.success('Content created');
      } else {
        await adminService.updateContent(selectedItem.id, data);
        toast.success('Content updated');
      }
      setShowEditor(false);
      loadData();
    } catch (error) {
      toast.error('Failed to save content');
    }
  };

  const handleDelete = async (contentId) => {
    if (!confirm('Are you sure you want to delete this content?')) return;
    
    try {
      await adminService.deleteContent(contentId);
      toast.success('Content deleted');
      loadData();
    } catch (error) {
      toast.error('Failed to delete content');
    }
  };

  const handleSeedContent = async () => {
    try {
      const result = await adminService.seedContent();
      toast.success(result.message);
      loadData();
    } catch (error) {
      toast.error('Failed to seed content');
    }
  };

  if (isLoading) {
    return (
      <div className="flex items-center justify-center min-h-[400px]">
        <span className="spinner w-12 h-12"></span>
      </div>
    );
  }

  const existingKeys = new Set(contentItems.map((c) => c.key));
  const missingKeys = contentKeys.filter((k) => !existingKeys.has(k.key));

  return (
    <div>
      <div className="flex items-center justify-between mb-6">
        <h1 className="text-2xl font-bold">Site Content</h1>
        <div className="flex gap-2">
          {contentItems.length === 0 && (
            <button onClick={handleSeedContent} className="btn btn-secondary">
              Seed Default Content
            </button>
          )}
        </div>
      </div>

      {/* Missing Content Keys */}
      {missingKeys.length > 0 && (
        <div className="card mb-6 bg-yellow-500/10 border-yellow-500/30">
          <h3 className="text-lg font-semibold mb-3 text-yellow-400">
            Missing Content ({missingKeys.length})
          </h3>
          <div className="flex flex-wrap gap-2">
            {missingKeys.map((key) => (
              <button
                key={key.key}
                onClick={() => handleCreateFromKey(key)}
                className="px-3 py-1 bg-yellow-500/20 text-yellow-400 rounded hover:bg-yellow-500/30 text-sm"
              >
                + {key.name}
              </button>
            ))}
          </div>
        </div>
      )}

      {/* Content List */}
      <div className="space-y-4">
        {contentItems.map((item) => (
          <div key={item.id} className="card">
            <div className="flex items-start justify-between">
              <div className="flex-1">
                <div className="flex items-center gap-2 mb-1">
                  <h3 className="text-lg font-semibold">{item.title}</h3>
                  <span className="px-2 py-0.5 bg-gray-700 text-gray-400 text-xs rounded">
                    {item.key}
                  </span>
                  {item.is_published ? (
                    <span className="px-2 py-0.5 bg-green-500/20 text-green-400 text-xs rounded">
                      Published
                    </span>
                  ) : (
                    <span className="px-2 py-0.5 bg-yellow-500/20 text-yellow-400 text-xs rounded">
                      Draft
                    </span>
                  )}
                </div>
                <p className="text-gray-400 text-sm">
                  Type: {item.content_type} | Updated:{' '}
                  {new Date(item.updated_at).toLocaleDateString()}
                </p>
                {item.content && (
                  <p className="text-gray-500 text-sm mt-2 line-clamp-2">
                    {item.content.replace(/<[^>]*>/g, '').slice(0, 200)}...
                  </p>
                )}
              </div>
              <div className="flex gap-2">
                <button
                  onClick={() => handleEdit(item)}
                  className="btn btn-secondary btn-sm"
                >
                  Edit
                </button>
                <button
                  onClick={() => handleDelete(item.id)}
                  className="text-red-400 hover:text-red-300 px-2"
                >
                  🗑
                </button>
              </div>
            </div>
          </div>
        ))}
      </div>

      {contentItems.length === 0 && missingKeys.length === 0 && (
        <div className="text-center py-12 text-gray-400">
          No content configured. Click "Seed Default Content" to get started.
        </div>
      )}

      {showEditor && selectedItem && (
        <ContentEditor
          item={selectedItem}
          onSave={handleSave}
          onClose={() => setShowEditor(false)}
        />
      )}
    </div>
  );
}

function ContentEditor({ item, onSave, onClose }) {
  const [formData, setFormData] = useState({
    title: item.title || '',
    content: item.content || '',
    content_type: item.content_type || 'html',
    meta_title: item.meta_title || '',
    meta_description: item.meta_description || '',
    is_published: item.is_published ?? true,
  });

  const handleSubmit = (e) => {
    e.preventDefault();
    onSave(formData);
  };

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 overflow-auto py-8">
      <div className="card w-full max-w-4xl max-h-[90vh] overflow-auto">
        <div className="flex items-center justify-between mb-4">
          <div>
            <h2 className="text-xl font-semibold">
              {item.isNew ? 'Create Content' : 'Edit Content'}
            </h2>
            <p className="text-gray-400 text-sm">{item.key}</p>
          </div>
          <button onClick={onClose} className="text-gray-400 hover:text-white">
            ✕
          </button>
        </div>

        <form onSubmit={handleSubmit} className="space-y-4">
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-sm text-gray-400 mb-1">Title *</label>
              <input
                type="text"
                value={formData.title}
                onChange={(e) => setFormData({ ...formData, title: e.target.value })}
                className="input w-full"
                required
              />
            </div>
            <div>
              <label className="block text-sm text-gray-400 mb-1">Content Type</label>
              <select
                value={formData.content_type}
                onChange={(e) =>
                  setFormData({ ...formData, content_type: e.target.value })
                }
                className="input w-full"
              >
                <option value="html">HTML</option>
                <option value="markdown">Markdown</option>
                <option value="plain_text">Plain Text</option>
                <option value="json">JSON</option>
              </select>
            </div>
          </div>

          <div>
            <label className="block text-sm text-gray-400 mb-1">Content</label>
            <textarea
              value={formData.content}
              onChange={(e) => setFormData({ ...formData, content: e.target.value })}
              className="input w-full font-mono text-sm"
              rows={15}
            />
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-sm text-gray-400 mb-1">Meta Title</label>
              <input
                type="text"
                value={formData.meta_title}
                onChange={(e) =>
                  setFormData({ ...formData, meta_title: e.target.value })
                }
                className="input w-full"
              />
            </div>
            <div>
              <label className="block text-sm text-gray-400 mb-1">Meta Description</label>
              <input
                type="text"
                value={formData.meta_description}
                onChange={(e) =>
                  setFormData({ ...formData, meta_description: e.target.value })
                }
                className="input w-full"
              />
            </div>
          </div>

          <label className="flex items-center gap-2">
            <input
              type="checkbox"
              checked={formData.is_published}
              onChange={(e) =>
                setFormData({ ...formData, is_published: e.target.checked })
              }
            />
            <span className="text-sm">Published</span>
          </label>

          <div className="flex gap-2 pt-4">
            <button type="submit" className="btn btn-primary flex-1">
              Save
            </button>
            <button type="button" onClick={onClose} className="btn btn-secondary flex-1">
              Cancel
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

export default SiteContentManagement;
